import { useEffect, useState } from 'react';

import { Card } from '@/components/Card/Card';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

import type { ScoreSummary } from './scoringUtils';
import styles from './ScoreSummaryBanner.module.css';

interface ScoreSummaryBannerProps {
  scoreSummary: ScoreSummary;
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function getTone(percentage: number) {
  if (percentage >= 70) {
    return 'success';
  }

  if (percentage >= 40) {
    return 'warning';
  }

  return 'error';
}

function getRows(scoreSummary: ScoreSummary) {
  return [
    {
      key: 'homework',
      label: 'Homework',
      maxScore: scoreSummary.homeworkMaxScore,
      percentage: scoreSummary.homeworkPercentage,
      studentScore: scoreSummary.homeworkStudentScore,
    },
    {
      key: 'pre-lecture',
      label: 'Pre-lecture',
      maxScore: scoreSummary.preLectureMaxScore,
      percentage: scoreSummary.preLecturePercentage,
      studentScore: scoreSummary.preLectureStudentScore,
    },
  ].filter((row) => row.maxScore > 0);
}

function useAnimatedNumber(target: number) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (prefersReducedMotion) {
      setValue(target);
      return undefined;
    }

    if (value === target) {
      return undefined;
    }

    let animationFrame = 0;
    let animationStart: number | null = null;
    const startValue = value;
    const duration = 420;

    const step = (timestamp: number) => {
      if (animationStart === null) {
        animationStart = timestamp;
      }

      const progress = Math.min(1, (timestamp - animationStart) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setValue(startValue + (target - startValue) * eased);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(step);
      }
    };

    animationFrame = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [prefersReducedMotion, target, value]);

  return prefersReducedMotion ? target : value;
}

export function ScoreSummaryBanner({ scoreSummary }: ScoreSummaryBannerProps) {
  const rows = getRows(scoreSummary);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card
      aria-labelledby="score-summary-heading"
      as="section"
      className={styles.card}
      variant="metric"
    >
      <div className={styles.header}>
        <p className={styles.eyebrow}>Review Summary</p>
        <h2 className={styles.title} id="score-summary-heading">
          Your score
        </h2>
      </div>

      <div className={styles.rows}>
        {rows.map((row) => (
          <AnimatedScoreRow key={row.key} row={row} />
        ))}
      </div>
    </Card>
  );
}

function AnimatedScoreRow({
  row,
}: {
  row: {
    key: string;
    label: string;
    maxScore: number;
    percentage: number;
    studentScore: number;
  };
}) {
  const animatedPercentage = useAnimatedNumber(row.percentage);
  const animatedScore = useAnimatedNumber(row.studentScore);

  return (
    <div className={styles.row}>
      <div className={styles.rowHeader}>
        <div>
          <p className={styles.rowLabel}>{row.label}</p>
          <p className={styles.scoreValue}>
            {formatScore(animatedScore)} <span>/ {formatScore(row.maxScore)}</span>
          </p>
        </div>
        <p className={styles.percentage} data-tone={getTone(animatedPercentage)}>
          {Math.round(animatedPercentage)}%
        </p>
      </div>

      <div
        aria-label={`${row.label} score ${Math.round(animatedPercentage)} percent`}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={Math.round(animatedPercentage)}
        className={styles.progress}
        role="progressbar"
      >
        <span
          className={styles.progressFill}
          data-tone={getTone(animatedPercentage)}
          style={{
            width: `${Math.max(0, Math.min(100, animatedPercentage))}%`,
          }}
        />
      </div>
    </div>
  );
}
